import { type HTMLMotionProps, motion, useInView, useReducedMotion } from 'framer-motion';
import { type ReactNode, useRef } from 'react';
import { MOTION_DURATION, MOTION_EASING, VIEWPORT_ONCE } from './motion-presets';

interface StaggerContainerProps extends HTMLMotionProps<'div'> {
  children: ReactNode;
  staggerDelay?: number;
  triggerOnce?: boolean;
}

interface StaggerItemProps extends HTMLMotionProps<'div'> {
  children: ReactNode;
}

export function StaggerContainer({
  children,
  staggerDelay = 0.08,
  triggerOnce = true,
  ...props
}: StaggerContainerProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const isInView = useInView(ref, triggerOnce ? VIEWPORT_ONCE : undefined);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView || prefersReducedMotion ? 'visible' : 'hidden'}
      variants={{
        hidden: { opacity: prefersReducedMotion ? 1 : 0 },
        visible: {
          opacity: 1,
          transition: { staggerChildren: prefersReducedMotion ? 0 : staggerDelay },
        },
      }}
      style={{ willChange: prefersReducedMotion ? 'auto' : 'opacity' }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, ...props }: StaggerItemProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      variants={{
        hidden: {
          opacity: prefersReducedMotion ? 1 : 0,
          y: prefersReducedMotion ? 0 : 18,
          filter: prefersReducedMotion ? 'blur(0px)' : 'blur(6px)',
        },
        visible: {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          transition: {
            duration: prefersReducedMotion ? 0 : MOTION_DURATION.base,
            ease: MOTION_EASING,
          },
        },
      }}
      style={{ willChange: prefersReducedMotion ? 'auto' : 'transform, opacity, filter' }}
      {...props}
    >
      {children}
    </motion.div>
  );
}
